import { FormikProps } from 'formik';
import { Calendar, InputFormik, Button } from '../../Common';
import { useEffect, useState, useRef } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from '../../../hooks/useTranslation';
import { DisabledDaysWithMessage } from '../../../commonTypes/acs';
import { HolidayDto } from '../../../commonTypes/organization.types';

interface IDatePickerInputFormikProps<T> {
    context: FormikProps<T>;
    changeDate?: (date: DateTime | null) => void;
    date?: Date;
    id?: string;
    label?: string;
    placeholder?: string;
    onPeriodChange: (from: string, to: string) => void;
    disabled?: boolean;
    clearData?: () => void;
    className?: string;
    isRangeMode?: boolean;
    fromDateId?: string;
    toDateId?: string;
    disabledDays?: DisabledDaysWithMessage[];
    holidays?: HolidayDto[];
    openAbove?: boolean;
    enablePastDays?: boolean;
    isRequired?: boolean;
}

export const DatePickerInputFormik = <T extends object>({
    id,
    context,
    date,
    changeDate,
    label,
    placeholder,
    onPeriodChange,
    disabled,
    clearData,
    className,
    isRangeMode = false,
    fromDateId,
    toDateId,
    disabledDays = [],
    holidays,
    openAbove = false,
    enablePastDays = true,
    isRequired = true,
}: IDatePickerInputFormikProps<T>) => {
    const [displayCalendar, setDisplayCalendar] = useState(false);
    const { t } = useTranslation();
    const calendarRef = useRef<HTMLDivElement>(null);

    const [displayDateCalendarLeft, setDisplayDateCalendarLeft] = useState(DateTime.now().startOf('day'));
    const [displayDateCalendarRight, setDisplayDateCalendarRight] = useState(DateTime.now().startOf('day'));
    const lastSyncedDateRef = useRef<number | null>(null);

    useEffect(() => {
        onPeriodChange(displayDateCalendarLeft.startOf('month').toISO(), displayDateCalendarLeft.plus({ month: 1 }).endOf('month').toISO());
    }, [displayDateCalendarLeft.toISO(), displayDateCalendarRight.toISO()]);

    useEffect(() => {
        if (isRangeMode || !date || isNaN(date.getTime())) {
            return;
        }

        const time = date.getTime();
        if (lastSyncedDateRef.current === time) {
            return;
        }

        lastSyncedDateRef.current = time;
        const dateLux = DateTime.fromJSDate(date);
        setDisplayDateCalendarLeft(dateLux.startOf('day'));
        setDisplayDateCalendarRight(dateLux.startOf('day'));
    }, [date, isRangeMode]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setDisplayCalendar(false);
            }
        };

        if (displayCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [displayCalendar]);

    const handleDisplayDate = (_changeDate: DateTime, nextBackAction?: string) => {
        if (nextBackAction === 'right') {
            setDisplayDateCalendarLeft(prev => prev.plus({ months: 1 }));
            setDisplayDateCalendarRight(prev => prev.plus({ months: 1 }));
        }

        if (nextBackAction === 'left') {
            setDisplayDateCalendarLeft(prev => prev.minus({ months: 1 }));
            setDisplayDateCalendarRight(prev => prev.minus({ months: 1 }));
        }
    };

    const handleDateSelection = (selectedDate: DateTime) => {
        if (changeDate) {
            changeDate(selectedDate);
        }
        setDisplayCalendar(false);
    };

    const handleRangeDateSelection = (selectedDate: DateTime) => {
        if (!fromDateId || !toDateId) return;

        const fromDate = context.values[fromDateId as keyof T];
        const toDate = context.values[toDateId as keyof T];
        const startDate = fromDate ? DateTime.fromJSDate(new Date(fromDate as string)) : null;
        const endDate = toDate ? DateTime.fromJSDate(new Date(toDate as string)) : null;

        if (startDate !== null && endDate !== null) {
            context.setFieldValue(fromDateId, selectedDate.toJSDate());
            context.setFieldValue(toDateId, null);
            return;
        }

        if (startDate === null) {
            context.setFieldValue(fromDateId, selectedDate.toJSDate());
            return;
        }

        if (startDate !== null && endDate === null) {
            if (selectedDate < startDate) {
                context.setFieldValue(fromDateId, selectedDate.toJSDate());
                context.setFieldValue(toDateId, startDate.toJSDate());
            } else {
                context.setFieldValue(toDateId, selectedDate.toJSDate());
            }
            setDisplayCalendar(false);

            if (onPeriodChange) {
                const finalStartDate = selectedDate < startDate ? selectedDate : startDate;
                const finalEndDate = selectedDate < startDate ? startDate : selectedDate;
                onPeriodChange(finalStartDate.toISO(), finalEndDate.toISO());
            }
        }
    };

    if (isRangeMode && fromDateId && toDateId) {
        const fromDate = context.values[fromDateId as keyof T];
        const toDate = context.values[toDateId as keyof T];
        const startDate = fromDate ? DateTime.fromJSDate(new Date(fromDate as string)) : null;
        const endDate = toDate ? DateTime.fromJSDate(new Date(toDate as string)) : null;

        const formattedDateRange =
            startDate && endDate
                ? `${startDate.toFormat('MMM dd, yyyy')} - ${endDate.toFormat('MMM dd, yyyy')}`
                : startDate
                ? startDate.toFormat('MMM dd, yyyy')
                : '';

        return (
            <div
                className={`date-picker-input ${displayCalendar ? 'open' : ''} ${openAbove ? 'date-picker-input--open-above' : ''} ${
                    className || ''
                }`}
                ref={calendarRef}
            >
                <div className="date-picker-input__area">
                    <InputFormik
                        context={context}
                        id={fromDateId}
                        iconSrc="/svg/calendar-gray.svg"
                        label="Date range"
                        iconOnClick={() => setDisplayCalendar(prev => !prev)}
                        isRequired={isRequired}
                        isDisabled={disabled}
                        value={formattedDateRange}
                        onChange={() => {}}
                    />
                    {displayCalendar && (
                        <div className={`date-picker-input__calendar-wrapper ${openAbove ? 'date-picker-input__calendar-wrapper--above' : ''}`}>
                            <div className="calendar-container">
                                <Calendar
                                    displayDate={displayDateCalendarLeft}
                                    changeMonth={handleDisplayDate}
                                    startDate={startDate}
                                    endDate={endDate}
                                    activeArrows={{
                                        active: true,
                                        firstCalendarLeftButton: true,
                                        secondCalendarRightButton: true,
                                    }}
                                    onClick={handleRangeDateSelection}
                                    enablePastDays={enablePastDays}
                                    disabledWeekends={false}
                                    disabledDays={disabledDays}
                                    holidays={holidays}
                                />
                                {clearData && (
                                    <Button
                                        className="clear-button"
                                        variant="text"
                                        color="blue"
                                        text={t('common.clear')}
                                        onClick={() => {
                                            clearData();
                                            setDisplayCalendar(false);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const formattedDate = date && !isNaN(date.getTime()) ? DateTime.fromJSDate(date).toFormat('MMMM dd, yyyy') : '';

    return (
        <div
            className={`date-picker-input ${displayCalendar ? 'open' : ''} ${openAbove ? 'date-picker-input--open-above' : ''} ${className || ''}`}
            ref={calendarRef}
        >
            <div className="date-picker-input__area">
                <InputFormik
                    context={context}
                    id={id || ''}
                    iconSrc="/svg/calendar-gray.svg"
                    label={label}
                    placeholder={placeholder}
                    iconOnClick={() => setDisplayCalendar(prev => !prev)}
                    isRequired={isRequired}
                    isDisabled={disabled}
                    value={formattedDate}
                    onChange={() => {}}
                />
                {displayCalendar && (
                    <div className={`date-picker-input__calendar-wrapper ${openAbove ? 'date-picker-input__calendar-wrapper--above' : ''}`}>
                        <div className="calendar-container">
                            <Calendar
                                displayDate={displayDateCalendarLeft}
                                changeMonth={handleDisplayDate}
                                singleDate={date ? DateTime.fromJSDate(date) : undefined}
                                activeArrows={{
                                    active: true,
                                    firstCalendarLeftButton: true,
                                    secondCalendarRightButton: true,
                                }}
                                onClick={handleDateSelection}
                                enablePastDays={enablePastDays}
                                disabledWeekends={false}
                            />
                            {clearData && (
                                <Button
                                    className="clear-button"
                                    variant="text"
                                    text={t('common.clear')}
                                    onClick={() => {
                                        clearData();
                                        setDisplayCalendar(false);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
